import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { WindowService } from '../common/window.service';
import { LoggerService } from '../logger';
import { NotificationService } from '../notification';
import { ChunkLoadingError } from './chunk-loading.error';

@Injectable()
export class ErrorsHandler implements ErrorHandler {
  private resourcesToIgnore = ['dc.services.visualstudio.com', 'login.microsoftonline.com'];
  private readonly chunkReloadSessionKey = 'farmcloud.chunk-reload-attempted';

  constructor(
    private readonly injector: Injector,
    private readonly router: Router
  ) {}

  handleError(error: Error | HttpErrorResponse) {
    const ngZone = this.injector.get(NgZone);

    if (this.handleChunkLoadError(error)) {
      return;
    }

    if (error instanceof HttpErrorResponse) {
      if (this.isIgnoredResource(error.url)) {
        return;
      }

      if (error.status == HttpStatusCode.NotFound) {
        return ngZone.run(() => {
          return this.router.navigateByUrl('notFound', { skipLocationChange: true });
        });
      }
    }

    // Ignore error from confirmation dialog
    if (
      error.message &&
      (error.message.indexOf('Uncaught (in promise): [object Number]') === 0 ||
        error.message.indexOf('Uncaught (in promise): [object Boolean]') === 0)
    ) {
      return;
    }

    // Ignore error from clarity
    if (error.message && error.message.includes('Clarity')) {
      console.error(error);
      return;
    }

    // Log the error
    try {
      const notificationService = this.injector.get(NotificationService);
      ngZone.run(() => notificationService.error(error));
      console.error(error);
      const appInsightsService = this.injector.get(LoggerService);
      appInsightsService.trackException(error);
    } catch (error) {
      console.error(error);
    }
  }

  private handleChunkLoadError(error: Error | HttpErrorResponse) {
    if (!ChunkLoadingError.isChunkLoadingError(error)) {
      return false;
    }

    console.error(error);

    if (sessionStorage.getItem(this.chunkReloadSessionKey)) {
      return false;
    }

    sessionStorage.setItem(this.chunkReloadSessionKey, 'true');
    const windowService = this.injector.get(WindowService);
    windowService.refreshPage();

    return true;
  }

  private isIgnoredResource(url: string) {
    if (!this.resourcesToIgnore) {
      return false;
    }

    for (const resourceToIgnore of this.resourcesToIgnore) {
      if (url.indexOf(resourceToIgnore) > -1) {
        return true;
      }
    }

    return false;
  }
}
